import * as T from 'three';

/** Secondary motion for the six back crystals, solved in the wing group's own
 * (torso) frame. Each module is a rigid arm hinged at its shoulder root and
 * the authored formation is its rest direction. Two swings are added to it:
 *
 *  - LAG: while the formation turns an arm, the blade keeps its heading and a
 *    spring recovers it, with a little overshoot. Per blade, like the lanes
 *    the formation authors, so neighbours never close on each other.
 *  - FORCED: the body's acceleration and rotation and the airflow. Felt by
 *    each side as one wing (a small per-blade ripple remains), so the fan
 *    moves together rather than shimmering blade by blade.
 *
 * Both are underdamped and soft-limited. With `lag: false` an upstream
 * follower supplies the first part and only the forced swing is added. */
export interface CrystalWingInertiaInput {
  /** Group-local rest targets (the formation). */
  targets: readonly T.Vector3[];
  rotations: readonly T.Quaternion[];
  /** Wing group origin and rotation in the stable simulation frame. */
  anchorPosition: T.Vector3;
  anchorRotation: T.Quaternion;
  /** World units per group-local unit (the group's uniform scale). */
  scale: number;
  backOffset: number;
  /** 0 free .. 1 braced (weapon focus, compact tuck, tight cape). */
  brace: number;
  /** False when an upstream follower already lags the formation (the
   * tethered CrystalWingDynamics): only the forced swing is added. */
  lag?: boolean;
}

const RANK_OMEGA=[12,10.5,9.2];
const RANK_LIMIT=[.26,.32,.38];
const RANK_TURBULENCE=[1,1.35,1.7];
const DAMPING=.5;
/** Streaming starts above this airspeed; below it the formation owns the fan. */
const AIR_REFERENCE=26;
const AIR_GAIN=2.1,AIR_CLAMP=6;
/** Angular acceleration of the airflow sway at 66 m/s, rad/s². */
const TURBULENCE=6;
const MAX_LINEAR=90,MAX_ANGULAR=40;
/** Target turns above this (radians per frame) are placements, not motion. */
const MAX_TARGET_STEP=.45;
const REACH_RATE=16;
/** The lag stays inside the lanes the formations author for each blade. */
const LAG_LIMIT=.09,LAG_OMEGA=16;
/** Each blade keeps this much of its own forced response; sides share half. */
const RIPPLE=.35,SIDE_SHARE=.5;
/** Render positions carry frame-pacing noise that a raw second difference
 * turns into shimmer; the wing only needs the sustained part of the motion. */
const BODY_SMOOTHING=.1;
/** Airflow sway: smooth value noise (new random keys at these rates, eased
 * between with continuous velocity) — a sway, never a per-frame shake. */
const SIDE_SWAY_HZ=1.6,BLADE_SWAY_HZ=2.2;

/** C¹ value noise in three channels: random keys joined by smootherstep. */
class SmoothNoise {
  readonly value=new T.Vector3();
  private readonly from=new T.Vector3();
  private readonly to=new T.Vector3();
  private phase=1;
  constructor(private readonly rate:number){}
  reset():void{this.from.set(0,0,0);this.to.set(0,0,0);this.value.set(0,0,0);this.phase=1;}
  step(dt:number,normal:()=>number):T.Vector3{
    this.phase+=dt*this.rate;
    while(this.phase>=1){this.phase-=1;this.from.copy(this.to);this.to.set(normal(),normal(),normal());}
    const u=this.phase,k=u*u*u*(u*(u*6-15)+10);
    return this.value.lerpVectors(this.from,this.to,k);
  }
}

interface Swing {theta:T.Vector3;rate:T.Vector3;soft:T.Vector3;}
const swing=():Swing=>({theta:new T.Vector3(),rate:new T.Vector3(),soft:new T.Vector3()});

export class CrystalWingInertia {
  readonly positions:T.Vector3[];
  readonly rotations:T.Quaternion[];
  private readonly lag:Swing[];
  private readonly forced:Swing[];
  private readonly total:T.Vector3[];
  private readonly noise:SmoothNoise[];
  private readonly sideNoise=[new SmoothNoise(SIDE_SWAY_HZ),new SmoothNoise(SIDE_SWAY_HZ*1.13)];
  private readonly previousArm:T.Vector3[];
  private readonly reach:Float64Array;
  private readonly previousPosition=new T.Vector3();
  private readonly previousRotation=new T.Quaternion();
  private readonly velocity=new T.Vector3();
  private readonly smoothVelocity=new T.Vector3();
  private readonly acceleration=new T.Vector3();
  private readonly angularVelocity=new T.Vector3();
  private readonly angularAcceleration=new T.Vector3();
  private readonly airflow=new T.Vector3();
  private seeded=false;
  private kinematicsSeeded=0;
  private seed=0x2f6b3a1;
  private readonly v=new T.Vector3();
  private readonly w=new T.Vector3();
  private readonly root=new T.Vector3();
  private readonly arm=new T.Vector3();
  private readonly force=new T.Vector3();
  private readonly torque=new T.Vector3();
  private readonly mean=[new T.Vector3(),new T.Vector3(),new T.Vector3()];
  private readonly inverse=new T.Quaternion();
  private readonly delta=new T.Quaternion();
  private readonly turn=new T.Quaternion();
  constructor(private readonly count=6){
    const vectors=()=>Array.from({length:count},()=>new T.Vector3());
    this.positions=vectors();this.rotations=Array.from({length:count},()=>new T.Quaternion());
    this.lag=Array.from({length:count},swing);this.forced=Array.from({length:count},swing);
    this.total=vectors();this.noise=Array.from({length:count},(_,i)=>new SmoothNoise(BLADE_SWAY_HZ*(1+.07*i)));this.previousArm=vectors();
    this.reach=new Float64Array(count);
  }
  reset():void{this.seeded=false;this.kinematicsSeeded=0;}
  /** Airspeed seen by the wing group last update, m/s (smoothed). */
  get airspeed():number{return this.airflow.length();}

  update(delta:number,input:Readonly<CrystalWingInertiaInput>):void{
    const {targets,rotations,anchorPosition,anchorRotation}=input;
    const scale=input.scale>1e-6?input.scale:1;
    const dt=Number.isFinite(delta)?delta:0;
    if(!this.seeded||dt>.2){
      for(let i=0;i<this.count;i++){
        this.positions[i].copy(targets[i]);this.rotations[i].copy(rotations[i]);
        for(const s of [this.lag[i],this.forced[i]]){s.theta.set(0,0,0);s.rate.set(0,0,0);s.soft.set(0,0,0);}
        this.total[i].set(0,0,0);this.noise[i].reset();this.reach[i]=0;
        this.rootOf(i,input.backOffset,this.root);this.previousArm[i].copy(targets[i]).sub(this.root).normalize();
      }
      for(const n of this.sideNoise)n.reset();
      this.previousPosition.copy(anchorPosition);this.previousRotation.copy(anchorRotation);
      this.velocity.set(0,0,0);this.smoothVelocity.set(0,0,0);this.acceleration.set(0,0,0);
      this.angularVelocity.set(0,0,0);this.angularAcceleration.set(0,0,0);this.airflow.set(0,0,0);
      this.seeded=true;this.kinematicsSeeded=0;return;
    }
    if(dt<=0){this.combine(input);return;}
    this.measureBody(dt,anchorPosition,anchorRotation,scale);
    const brace=T.MathUtils.clamp(input.brace,0,1);
    const airspeed=this.airflow.length();
    const stream=Math.min(AIR_CLAMP,Math.max(0,airspeed*airspeed-AIR_REFERENCE*AIR_REFERENCE)/(AIR_REFERENCE*AIR_REFERENCE))*AIR_GAIN*(1-.8*brace);
    const buffet=TURBULENCE*Math.min(1.4,(airspeed/66)**2)*(1-.85*brace);
    // Airflow sway: smooth noise mostly shared by a side, filtered again by
    // the springs.
    const normal=()=>this.gaussian();
    for(const n of this.sideNoise)n.step(dt,normal);
    for(let i=0;i<this.count;i++){
      const rank=i%3,omega=RANK_OMEGA[rank]*(1+1.6*brace),zeta=DAMPING+.35*brace,limit=RANK_LIMIT[rank]*(1-.65*brace);
      this.rootOf(i,input.backOffset,this.root);
      this.arm.copy(targets[i]).sub(this.root);
      const length=Math.max(.12,this.arm.length());
      this.arm.divideScalar(length);
      // LAG: the blade keeps its heading while the formation turns its arm.
      this.w.crossVectors(this.previousArm[i],this.arm);
      const turned=Math.asin(Math.min(1,this.w.length()));
      const follow=input.lag!==false;
      if(follow&&turned>1e-7&&turned<MAX_TARGET_STEP)this.lag[i].theta.addScaledVector(this.w,-turned/this.w.length());
      this.previousArm[i].copy(this.arm);
      const reach=this.reach[i];
      this.reach[i]=follow&&reach>0&&Math.abs(length-reach)<.25?reach+(length-reach)*(1-Math.exp(-dt*REACH_RATE)):length;
      // One filter for every blade and nearly critical: all targets are
      // delayed alike, so the authored lanes between neighbours survive.
      this.integrate(this.lag[i],null,LAG_OMEGA*(1+brace),.85,Math.min(limit,LAG_LIMIT),dt);
      // FORCED: everything the body and the air do, as an angular
      // acceleration about the root.
      this.noise[i].step(dt,normal);
      this.force.copy(this.acceleration).negate();
      if(stream>0)this.force.addScaledVector(this.airflow,stream/Math.max(1,airspeed));
      this.torque.crossVectors(this.arm,this.force).divideScalar(length).sub(this.angularAcceleration);
      this.torque.addScaledVector(this.sideNoise[i<3?0:1].value,buffet*RANK_TURBULENCE[rank]*.8).addScaledVector(this.noise[i].value,buffet*RANK_TURBULENCE[rank]*.25);
      this.torque.addScaledVector(this.arm,-this.torque.dot(this.arm));
      this.integrate(this.forced[i],this.torque,omega,zeta,limit,dt);
      // Neither swing spins the blade about its own arm.
      this.lag[i].soft.addScaledVector(this.arm,-this.lag[i].soft.dot(this.arm));
      this.forced[i].soft.addScaledVector(this.arm,-this.forced[i].soft.dot(this.arm));
    }
    this.combine(input);
  }

  private integrate(s:Swing,torque:T.Vector3|null,omega:number,zeta:number,limit:number,dt:number):void{
    const steps=Math.max(1,Math.ceil(dt*240)),h=dt/steps;
    for(let k=0;k<steps;k++){
      // Semi-implicit Euler at 240 Hz: stable for these stiffnesses.
      this.v.copy(s.theta).multiplyScalar(-omega*omega).addScaledVector(s.rate,-2*zeta*omega);
      if(torque)this.v.add(torque);
      s.rate.addScaledVector(this.v,h);s.theta.addScaledVector(s.rate,h);
    }
    // Soft limit: the swing saturates smoothly; far past it, it stops.
    const magnitude=s.theta.length();
    if(magnitude>limit*1.6){s.theta.multiplyScalar(limit*1.6/magnitude);const outward=s.rate.dot(s.theta)/s.theta.lengthSq();if(outward>0)s.rate.addScaledVector(s.theta,-outward);}
    const m=s.theta.length();
    s.soft.copy(s.theta);if(m>limit*.5)s.soft.multiplyScalar(limit*Math.tanh(m/limit)/m);
  }

  /** Lag per blade + forced swing felt as one wing per side. */
  private combine(input:Readonly<CrystalWingInertiaInput>):void{
    if(this.count===6){
      const [left,right,both]=this.mean;left.set(0,0,0);right.set(0,0,0);
      for(let i=0;i<3;i++){left.add(this.forced[i].soft);right.add(this.forced[i+3].soft);}
      left.divideScalar(3);right.divideScalar(3);both.copy(left).add(right).multiplyScalar(.5);
      for(let i=0;i<6;i++){
        const side=i<3?left:right;
        this.w.copy(side).sub(both).multiplyScalar(SIDE_SHARE).add(both);
        this.total[i].copy(this.forced[i].soft).sub(side).multiplyScalar(RIPPLE).add(this.w).add(this.lag[i].soft);
      }
    }else for(let i=0;i<this.count;i++)this.total[i].copy(this.forced[i].soft).add(this.lag[i].soft);
    for(let i=0;i<this.count;i++)this.pose(i,this.total[i],input,this.positions[i],this.rotations[i]);
  }

  private pose(i:number,theta:T.Vector3,input:Readonly<CrystalWingInertiaInput>,position:T.Vector3,rotation:T.Quaternion):void{
    this.rootOf(i,input.backOffset,this.root);
    const angle=theta.length();
    if(angle>1e-7)this.turn.setFromAxisAngle(this.w.copy(theta).divideScalar(angle),angle);else this.turn.identity();
    this.arm.copy(input.targets[i]).sub(this.root);
    const length=this.arm.length();
    if(this.reach[i]>0&&length>1e-6)this.arm.multiplyScalar(this.reach[i]/length);
    position.copy(this.arm).applyQuaternion(this.turn).add(this.root);
    rotation.copy(input.rotations[i]).premultiply(this.turn);
  }

  /** Blades hinge near the shoulder blade of their own side. */
  private rootOf(i:number,backOffset:number,out:T.Vector3):T.Vector3{
    return out.set((i<3?-1:1)*.09,.06,backOffset);
  }

  /** Smoothed body acceleration, angular acceleration and airflow in the
   * group frame, from the anchor's motion through the simulation frame. */
  private measureBody(dt:number,position:T.Vector3,rotation:T.Quaternion,scale:number):void{
    this.inverse.copy(rotation).invert();
    this.v.copy(position).sub(this.previousPosition).divideScalar(dt);
    this.delta.copy(this.previousRotation).invert().multiply(rotation);
    if(this.delta.w<0)this.delta.set(-this.delta.x,-this.delta.y,-this.delta.z,-this.delta.w);
    const sine=Math.hypot(this.delta.x,this.delta.y,this.delta.z),angle=2*Math.atan2(sine,this.delta.w);
    this.w.set(this.delta.x,this.delta.y,this.delta.z).multiplyScalar(sine>1e-9?angle/sine/dt:2/dt);
    // Origin shifts, teleports and respawns are discontinuities, not forces.
    const jumped=this.v.length()>250||this.w.length()>40;
    this.previousPosition.copy(position);this.previousRotation.copy(rotation);
    if(jumped){this.kinematicsSeeded=0;this.acceleration.set(0,0,0);this.angularAcceleration.set(0,0,0);return;}
    if(this.kinematicsSeeded<2){
      this.velocity.copy(this.v);this.smoothVelocity.copy(this.v);this.angularVelocity.copy(this.w);this.kinematicsSeeded++;
      this.airflow.copy(this.v).applyQuaternion(this.inverse).negate();return;
    }
    const smooth=1-Math.exp(-dt/BODY_SMOOTHING);
    this.force.copy(this.v).sub(this.velocity).divideScalar(dt).applyQuaternion(this.inverse).divideScalar(scale);
    if(this.force.length()>MAX_LINEAR)this.force.setLength(MAX_LINEAR);
    this.acceleration.lerp(this.force,smooth);
    this.force.copy(this.w).sub(this.angularVelocity).divideScalar(dt);
    if(this.force.length()>MAX_ANGULAR)this.force.setLength(MAX_ANGULAR);
    this.angularAcceleration.lerp(this.force,smooth);
    this.velocity.copy(this.v);this.angularVelocity.copy(this.w);
    this.smoothVelocity.lerp(this.v,smooth);
    this.airflow.copy(this.smoothVelocity).applyQuaternion(this.inverse).negate();
  }

  /** Deterministic standard normal (Box-Muller over a xorshift stream). */
  private gaussian():number{
    const next=()=>{let x=this.seed;x^=x<<13;x^=x>>>17;x^=x<<5;this.seed=x>>>0;return (this.seed+.5)/4294967296;};
    return Math.sqrt(-2*Math.log(next()))*Math.cos(2*Math.PI*next());
  }
}
